/**
 * Partners marquee, from hung-hola blocks/partners/view.js (docs/animation/partners.md): the logo lists move in an
 * endless loop. Every list moves by one set width (list + gap) in DIRECTION and gsap.utils.wrap keeps x inside
 * [-setWidth, 0), so each copy takes the place of the one next to it without a jump. x = -setWidth puts the copy
 * exactly where the first list was, so the first frame matches the still row in both directions.
 * - Lists are cloned (aria-hidden, empty alt) when two sets do not cover the track (a gallery with few logos).
 * - Slower under the mouse, pushed by the page scroll: the shared loop speed (assets/js/loop-motion.ts), with `follow`
 *   (owner 2026-10-20): scrolling down speeds it up its own way, scrolling up sends it fast the other way, still it
 *   glides back to its own way. Runs backwards on a negative timeScale, so the tween starts far into its repeats.
 * - Pauses off screen. ?still and prefers-reduced-motion keep the still row. GSAP: Nectar's (handle "gsap").
 */
import { loopMotion } from '../../assets/js/loop-motion';

const SPEED = 50; // px per second (owner, tuned by eye)
const DIRECTION = -1; // -1: right to left, 1: left to right

function init(wrapper: HTMLElement): void {
	const { gsap } = window;
	const track = wrapper.querySelector<HTMLElement>('[data-partners-track]');
	const lists = Array.from(wrapper.querySelectorAll<HTMLElement>('[data-partners-list]'));
	if (!gsap || !track || !lists.length) {
		return;
	}
	if (new URLSearchParams(window.location.search).has('still')) {
		return;
	}

	const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
	let tween: gsap.core.Tween | null = null;
	let inView = false;

	const stop = (): void => {
		tween?.kill();
		tween = null;
		gsap.set(lists, { clearProps: 'transform' });
	};

	const build = (): void => {
		const progress = tween ? tween.progress() : 0;
		stop();
		if (reduceMotion.matches) {
			return;
		}

		const style = getComputedStyle(track);
		const gap = parseFloat(style.columnGap) || 0;
		const setWidth = lists[0].getBoundingClientRect().width + gap;
		if (setWidth <= gap) {
			return;
		}

		// At x = -setWidth (the worst point of the loop in both directions) the last list must still reach the right edge
		const padding = parseFloat(style.paddingLeft) || 0;
		while ((lists.length - 1) * setWidth < track.clientWidth + gap - padding) {
			const copy = lists[0].cloneNode(true) as HTMLElement;
			copy.setAttribute('aria-hidden', 'true');
			copy.querySelectorAll('img').forEach((img) => (img.alt = ''));
			track.append(copy);
			lists.push(copy);
		}

		const wrapX = gsap.utils.wrap(-setWidth, 0);
		gsap.set(lists, { x: wrapX(-setWidth + DIRECTION * progress * setWidth) });
		tween = gsap.to(lists, {
			x: `${DIRECTION > 0 ? '+=' : '-='}${setWidth}`,
			duration: setWidth / SPEED,
			ease: 'none',
			repeat: -1,
			modifiers: { x: gsap.utils.unitize(wrapX) },
			paused: !inView,
		});
		tween.totalTime(tween.duration() * 1000); // room to run backwards (a reversed tween stops at its start)
	};

	loopMotion()?.add({ hover: track, follow: true, apply: (scale) => tween?.timeScale(scale) });

	// Rebuild when the track or the logos change size (breakpoint, logo images loading)
	let frame = 0;
	const resizeObserver = new ResizeObserver(() => {
		cancelAnimationFrame(frame);
		frame = requestAnimationFrame(build);
	});
	resizeObserver.observe(track);
	resizeObserver.observe(lists[0]);

	new IntersectionObserver(([entry]) => {
		inView = entry.isIntersecting;
		if (tween) {
			if (inView) {
				tween.resume(); // not play(): that turns a backwards run forwards
			} else {
				tween.pause();
			}
		}
	}).observe(track);

	reduceMotion.addEventListener('change', build);
}

document.querySelectorAll<HTMLElement>('[data-px-block="partners"]').forEach(init);
